import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { proposalsService } from "@/services/proposals.service";
import { customersService } from "@/services/customers.service";
import { assetsService } from "@/services/assets.service";
import { workOrdersService } from "@/services/work_orders.service";
import { getErrorMessage } from "@/services/api";

const NONE = "__none__";

function Field({ label, error, children, className = "", hint }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-sm font-medium">{label}</Label>
            {children}
            {hint && !error && <div className="text-xs text-muted-foreground">{hint}</div>}
            {error && <div className="text-xs text-danger">{error}</div>}
        </div>
    );
}

const schema = z.object({
    customer_id: z.string().min(1, "Selecione um cliente"),
    asset_id: z.string().optional().nullable(),
    work_order_id: z.string().optional().nullable(),
    title: z.string().optional().nullable(),
    validity_days: z.coerce.number().int().min(1).max(365).default(30),
    delivery_time_days: z.coerce.number().int().min(0).max(365).optional().nullable(),
    payment_terms: z.string().optional().nullable(),
    warranty: z.string().optional().nullable(),
    observations: z.string().optional().nullable(),
});

export default function ProposalNewPage() {
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            customer_id: "",
            asset_id: null,
            work_order_id: null,
            title: "",
            validity_days: 30,
            delivery_time_days: null,
            payment_terms: "",
            warranty: "90 dias contra defeitos de serviço",
            observations: "",
        },
    });

    const customerId = watch("customer_id");

    const customersQuery = useQuery({
        queryKey: ["customers-lite"],
        queryFn: () => customersService.list({ pageSize: 200, sortBy: "legal_name", sortOrder: "asc" }),
    });

    const assetsQuery = useQuery({
        queryKey: ["assets-by-customer", customerId],
        queryFn: () =>
            assetsService.list({
                pageSize: 100,
                customer_id: customerId,
                sortBy: "created_at",
                sortOrder: "desc",
            }),
        enabled: !!customerId,
    });

    const workOrdersQuery = useQuery({
        queryKey: ["work-orders-by-customer", customerId],
        queryFn: () =>
            workOrdersService.list({
                pageSize: 100,
                customer_id: customerId,
                sortBy: "created_at",
                sortOrder: "desc",
            }),
        enabled: !!customerId,
    });

    const create = useMutation({
        mutationFn: (payload) => proposalsService.create(payload),
        onSuccess: (proposal) => {
            toast.success(`Proposta ${proposal.number} criada.`);
            navigate(`/proposals/${proposal.id}`);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao criar proposta.")),
    });

    const onSubmit = (values) => {
        const payload = {
            customer_id: values.customer_id,
            asset_id: values.asset_id || null,
            work_order_id: values.work_order_id || null,
            title: values.title?.trim() || null,
            validity_days: values.validity_days,
            delivery_time_days: values.delivery_time_days || null,
            commercial_terms: {
                payment_terms: values.payment_terms?.trim() || null,
                warranty: values.warranty?.trim() || null,
                observations: values.observations?.trim() || null,
            },
        };
        create.mutate(payload);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="proposal-new-page">
            <PageHeader
                title="Nova Proposta Comercial"
                description="Crie uma proposta manualmente. Só o cliente é obrigatório — você pode vincular equipamento e OS agora ou depois."
                backTo="/proposals"
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Propostas", to: "/proposals" },
                    { label: "Nova" },
                ]}
                actions={
                    <>
                        <Button type="button" variant="ghost" onClick={() => navigate("/proposals")}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={create.isPending}
                            data-testid="proposal-save-button"
                        >
                            {create.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="mr-2 h-4 w-4" />
                            )}
                            Criar proposta
                        </Button>
                    </>
                }
            />

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Cliente e vínculos</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4">
                            <Field
                                label="Cliente *"
                                error={errors.customer_id?.message}
                                hint="Obrigatório. Todos os dados do cliente serão copiados para a proposta."
                            >
                                <Select
                                    value={watch("customer_id") || ""}
                                    onValueChange={(v) => {
                                        setValue("customer_id", v, { shouldValidate: true });
                                        // Reset asset/WO when customer changes
                                        setValue("asset_id", null);
                                        setValue("work_order_id", null);
                                    }}
                                >
                                    <SelectTrigger data-testid="proposal-customer-select">
                                        <SelectValue placeholder="Selecione um cliente" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(customersQuery.data?.items || []).map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.legal_name}
                                                {c.trade_name ? ` — ${c.trade_name}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>

                            <div className="grid gap-4 md:grid-cols-2">
                                <Field
                                    label="Equipamento (opcional)"
                                    hint={!customerId ? "Selecione o cliente primeiro" : ""}
                                >
                                    <Select
                                        disabled={!customerId}
                                        value={watch("asset_id") || NONE}
                                        onValueChange={(v) => setValue("asset_id", v === NONE ? null : v)}
                                    >
                                        <SelectTrigger data-testid="proposal-asset-select">
                                            <SelectValue placeholder="Nenhum" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NONE}>Nenhum</SelectItem>
                                            {(assetsQuery.data?.items || []).map((a) => (
                                                <SelectItem key={a.id} value={a.id}>
                                                    {a.internal_code} · {a.manufacturer || ""} {a.model || ""}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>

                                <Field
                                    label="Ordem de Serviço (opcional)"
                                    hint={!customerId ? "Selecione o cliente primeiro" : "Nem toda proposta vem de uma OS"}
                                >
                                    <Select
                                        disabled={!customerId}
                                        value={watch("work_order_id") || NONE}
                                        onValueChange={(v) => setValue("work_order_id", v === NONE ? null : v)}
                                    >
                                        <SelectTrigger data-testid="proposal-wo-select">
                                            <SelectValue placeholder="Nenhuma" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NONE}>Nenhuma</SelectItem>
                                            {(workOrdersQuery.data?.items || []).map((wo) => (
                                                <SelectItem key={wo.id} value={wo.id}>
                                                    {wo.number} · {wo.title || "OS"}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Cabeçalho da proposta</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4">
                            <Field label="Título / Objeto (opcional)">
                                <Input
                                    {...register("title")}
                                    placeholder="Ex.: Manutenção preventiva anual"
                                    data-testid="proposal-title"
                                />
                            </Field>
                            <div className="grid gap-4 md:grid-cols-2">
                                <Field
                                    label="Validade (dias) *"
                                    error={errors.validity_days?.message}
                                >
                                    <Input
                                        type="number"
                                        min="1"
                                        max="365"
                                        {...register("validity_days")}
                                        data-testid="proposal-validity-days"
                                    />
                                </Field>
                                <Field label="Prazo de entrega (dias)">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="365"
                                        {...register("delivery_time_days")}
                                        data-testid="proposal-delivery-days"
                                    />
                                </Field>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6 lg:col-span-1">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Condições comerciais</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4">
                            <Field label="Forma de pagamento">
                                <Input
                                    {...register("payment_terms")}
                                    placeholder="Ex.: 30/60/90 dias"
                                    data-testid="proposal-payment-terms"
                                />
                            </Field>
                            <Field label="Garantia">
                                <Input
                                    {...register("warranty")}
                                    data-testid="proposal-warranty"
                                />
                            </Field>
                            <Field label="Observações">
                                <Textarea
                                    rows={4}
                                    {...register("observations")}
                                    placeholder="Notas adicionais para o cliente"
                                    data-testid="proposal-observations"
                                />
                            </Field>
                        </CardContent>
                    </Card>
                    <Card className="rounded-xl border-dashed bg-muted/30 shadow-none">
                        <CardContent className="space-y-2 pt-4 text-xs text-muted-foreground">
                            <div className="text-sm font-medium text-foreground">Próximos passos</div>
                            <div>1. Após criar, você poderá adicionar serviços e peças.</div>
                            <div>2. Se desejar, vincule uma OS depois na tela de detalhes.</div>
                            <div>3. Envie para aprovação quando terminar o preenchimento.</div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </form>
    );
}
